import React, { useState, useRef, useEffect } from 'react';
import { useAgent } from '../../context/AgentContext';
import ReactMarkdown from 'react-markdown';
import {
  Send,
  FileText,
  ListChecks,
  AlertTriangle,
  FileBarChart,
  Download,
  Bot,
  User,
  ThumbsUp,
  ThumbsDown,
  Copy,
  Check
} from 'lucide-react';
import './ChatInterface.css';

const queryTypes = [
  { id: 'question', label: 'Ask Question', icon: Send, color: '#3B82F6' },
  { id: 'summary', label: 'Summarize', icon: FileText, color: '#10B981' },
  { id: 'action_items', label: 'Action Items', icon: ListChecks, color: '#8B5CF6' },
  { id: 'risk_analysis', label: 'Risk Analysis', icon: AlertTriangle, color: '#F59E0B' },
  { id: 'report', label: 'Generate Report', icon: FileBarChart, color: '#EF4444' }
];

const ChatInterface = () => {
  const { activeConversation, sendMessage, exportConversation, loading } = useAgent();
  const [input, setInput] = useState('');
  const [selectedQueryType, setSelectedQueryType] = useState('question');
  const [copiedId, setCopiedId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    scrollToBottom();
  }, [activeConversation?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading.query) return;

    const message = input.trim();
    setInput('');
    await sendMessage(message, selectedQueryType);
    inputRef.current?.focus();
  };

  const handleQuickAction = async (type) => {
    const prompts = {
      summary: 'Please provide a comprehensive summary of all uploaded documents.',
      action_items: 'Extract all action items, tasks, and to-dos from the documents.',
      risk_analysis: 'Perform a risk and compliance analysis on the documents.',
      report: 'Generate a detailed report based on the document contents.'
    };

    if (prompts[type]) {
      setSelectedQueryType(type);
      await sendMessage(prompts[type], type);
    }
  };

  const handleCopy = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleExport = async () => {
    if (activeConversation) {
      await exportConversation(activeConversation._id);
    }
  };

  return (
    <div className="chat-interface">
      <div className="chat-header">
        <div className="chat-title">
          <h3>{activeConversation?.title}</h3>
          <span className="message-count">
            {activeConversation?.messages?.length || 0} messages
          </span>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={handleExport}>
          <Download size={16} />
          Export PDF
        </button>
      </div>

      <div className="chat-messages">
        {activeConversation?.messages?.length === 0 ? (
          <div className="chat-welcome">
            <Bot size={48} className="welcome-icon" />
            <h3>How can I help you today?</h3>
            <p>Ask questions about your documents or use the quick actions below.</p>

            <div className="quick-actions">
              {queryTypes.slice(1).map((type) => (
                <button
                  key={type.id}
                  className="quick-action-btn"
                  onClick={() => handleQuickAction(type.id)}
                  disabled={loading.query}
                >
                  <type.icon size={20} style={{ color: type.color }} />
                  <span>{type.label}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          activeConversation?.messages?.map((message, index) => (
            <div
              key={message._id || index}
              className={`message ${message.role}`}
            >
              <div className="message-avatar">
                {message.role === 'user' ? (
                  <User size={18} />
                ) : (
                  <Bot size={18} />
                )}
              </div>
              <div className="message-content">
                <div className="message-header">
                  <span className="message-sender">
                    {message.role === 'user' ? 'You' : 'AI Assistant'}
                  </span>
                  {message.queryType && message.role === 'user' && (
                    <span className="message-type badge badge-primary">
                      {queryTypes.find(t => t.id === message.queryType)?.label}
                    </span>
                  )}
                </div>
                <div className="message-body">
                  {message.role === 'assistant' ? (
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                  ) : (
                    <p>{message.content}</p>
                  )}
                </div>
                {message.sources && message.sources.length > 0 && (
                  <div className="message-sources">
                    <span className="sources-label">Sources:</span>
                    {message.sources.map((source, idx) => (
                      <span key={idx} className="source-badge">
                        <FileText size={12} />
                        {source.documentName}
                      </span>
                    ))}
                  </div>
                )}
                {message.role === 'assistant' && (
                  <div className="message-actions">
                    <button
                      className="message-action-btn"
                      onClick={() => handleCopy(message.content, message._id)}
                      title="Copy response"
                    >
                      {copiedId === message._id ? (
                        <Check size={14} />
                      ) : (
                        <Copy size={14} />
                      )}
                    </button>
                    <button className="message-action-btn" title="Good response">
                      <ThumbsUp size={14} />
                    </button>
                    <button className="message-action-btn" title="Poor response">
                      <ThumbsDown size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}

        {loading.query && (
          <div className="message assistant">
            <div className="message-avatar">
              <Bot size={18} />
            </div>
            <div className="message-content">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-container">
        <div className="query-type-selector">
          {queryTypes.map((type) => (
            <button
              key={type.id}
              className={`query-type-btn ${selectedQueryType === type.id ? 'active' : ''}`}
              onClick={() => setSelectedQueryType(type.id)}
              title={type.label}
              style={{ '--type-color': type.color }}
            >
              <type.icon size={16} />
            </button>
          ))}
        </div>

        <form className="chat-input-form" onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="text"
            className="chat-input"
            placeholder={`${queryTypes.find(t => t.id === selectedQueryType)?.label}...`}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={loading.query}
          />
          <button
            type="submit"
            className="btn btn-primary send-btn"
            disabled={!input.trim() || loading.query}
          >
            {loading.query ? (
              <span className="spinner" />
            ) : (
              <Send size={18} />
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatInterface;
